import { NODE_TYPES, NODE, SKETCH_BOX } from '../../constants';
import { buildSocketsFromType, calcNodeHeight, buildFourSidedSockets, buildTriangleSockets } from '../../utils/socketLayout';
import { classifierBoxSize } from '../../utils/umlMembers';
import type { SocketDefinition, Size } from '../../types';

const NODE_WIDTH = NODE.DEFAULT_WIDTH;

const WAYPOINT_W = 40;
const WAYPOINT_H = 16;

export interface NodeBlueprint {
  size: Size;
  sockets: SocketDefinition[];
  label: string;
  data: Record<string, unknown>;
  /** True for types that should not trigger inline-edit after add */
  skipAutoEdit: boolean;
}

/**
 * NodeFactory centralises all per-type size, socket, label, and data
 * derivation so that node creation paths (panel drag, chord macro,
 * quick-create from edge-drag) all produce identical results without
 * duplicating switch logic.
 *
 * Adding a new node type only requires changes here (and in NODE_TYPES).
 */
export class NodeFactory {
  /**
   * Build a blueprint for the given node type and count of existing
   * same-type nodes (used to suffix the default label).
   */
  static create(type: string, sameTypeCount: number): NodeBlueprint {
    const meta = NODE_TYPES[type as keyof typeof NODE_TYPES] ?? NODE_TYPES.service;

    switch (type) {
      case 'diamond': {
        const size = { width: 160, height: 120 };
        return {
          size,
          sockets: buildFourSidedSockets(size.width, size.height),
          label: `${meta.label} ${sameTypeCount + 1}`,
          data: { color: meta.color },
          skipAutoEdit: false,
        };
      }

      case 'waypoint': {
        const size = { width: WAYPOINT_W, height: WAYPOINT_H };
        return {
          size,
          sockets: [
            { id: 'in-0', type: 'input' as const, dataType: 'data' as const, position: { x: 0, y: WAYPOINT_H / 2 }, order: 0 },
            { id: 'out-0', type: 'output' as const, dataType: 'data' as const, position: { x: WAYPOINT_W, y: WAYPOINT_H / 2 }, order: 0 },
          ],
          label: '',
          data: { color: meta.color },
          skipAutoEdit: true,
        };
      }

      case 'oval': {
        const size = { width: 160, height: 100 };
        return {
          size,
          sockets: buildFourSidedSockets(160, 100),
          label: `${meta.label} ${sameTypeCount + 1}`,
          data: { color: meta.color },
          skipAutoEdit: false,
        };
      }

      case 'rounded': {
        const size = { width: 160, height: 80 };
        return {
          size,
          sockets: buildFourSidedSockets(160, 80),
          label: `${meta.label} ${sameTypeCount + 1}`,
          data: { color: meta.color },
          skipAutoEdit: false,
        };
      }

      case 'cylinder': {
        const size = { width: 140, height: 100 };
        return {
          size,
          sockets: buildFourSidedSockets(140, 100),
          label: `${meta.label} ${sameTypeCount + 1}`,
          data: { color: meta.color },
          skipAutoEdit: false,
        };
      }

      case 'hexagon': {
        const size = { width: 140, height: 120 };
        return {
          size,
          sockets: buildFourSidedSockets(140, 120),
          label: `${meta.label} ${sameTypeCount + 1}`,
          data: { color: meta.color },
          skipAutoEdit: false,
        };
      }

      case 'triangle': {
        const size = { width: 140, height: 110 };
        return {
          size,
          sockets: buildTriangleSockets(140, 110),
          label: `${meta.label} ${sameTypeCount + 1}`,
          data: { color: meta.color },
          skipAutoEdit: false,
        };
      }

      case 'cross': {
        const size = { width: 120, height: 120 };
        return {
          size,
          sockets: buildFourSidedSockets(120, 120),
          label: `${meta.label} ${sameTypeCount + 1}`,
          data: { color: meta.color },
          skipAutoEdit: false,
        };
      }

      case 'text': {
        return {
          size: { width: 120, height: 32 },
          sockets: [],
          label: 'Text',
          data: { color: meta.color },
          skipAutoEdit: false,
        };
      }

      case 'note': {
        return {
          size: { width: 180, height: 120 },
          sockets: [],
          label: 'Note',
          data: { color: meta.color },
          skipAutoEdit: false,
        };
      }

      case 'arrow': {
        return {
          size: { width: 160, height: 16 },
          sockets: [],
          label: '',
          data: {
            color: meta.color,
            from: { x: 0, y: 0.5 },
            to: { x: 1, y: 0.5 },
            edgeType: 'arrow',
          },
          skipAutoEdit: true,
        };
      }

      case 'line': {
        return {
          size: { width: 160, height: 16 },
          sockets: [],
          label: '',
          data: {
            color: meta.color,
            from: { x: 0, y: 0.5 },
            to: { x: 1, y: 0.5 },
            edgeType: 'no-arrow',
          },
          skipAutoEdit: true,
        };
      }

      case 'box': {
        const size = { width: SKETCH_BOX.DEFAULT_WIDTH, height: SKETCH_BOX.DEFAULT_HEIGHT };
        return {
          size,
          sockets: buildFourSidedSockets(size.width, size.height),
          label: `Box ${sameTypeCount + 1}`,
          data: { color: meta.color },
          skipAutoEdit: false,
        };
      }

      case 'net_cloud':
        return magneticBlueprint(type, sameTypeCount, { width: 200, height: 110 });
      case 'net_subnet':
        return magneticBlueprint(type, sameTypeCount, { width: 220, height: 130 });
      case 'net_host':
        return magneticBlueprint(type, sameTypeCount, { width: 150, height: 90 });
      case 'net_firewall':
        return magneticBlueprint(type, sameTypeCount, { width: 140, height: 90 });
      case 'net_router':
        return magneticBlueprint(type, sameTypeCount, { width: 120, height: 120 });
      case 'net_lb':
        return magneticBlueprint(type, sameTypeCount, { width: 150, height: 90 });
      case 'net_switch':
        return magneticBlueprint(type, sameTypeCount, { width: 170, height: 56 });

      case 'pipe_source':
        return magneticBlueprint(type, sameTypeCount, { width: 150, height: 72 });
      case 'pipe_process':
        return magneticBlueprint(type, sameTypeCount, { width: 170, height: 72 });
      case 'pipe_store':
        return magneticBlueprint(type, sameTypeCount, { width: 150, height: 90 });
      case 'pipe_sink':
        return magneticBlueprint(type, sameTypeCount, { width: 150, height: 72 });
      case 'pipe_queue':
        return magneticBlueprint(type, sameTypeCount, { width: 150, height: 72 });

      case 'uml_class': {
        const extra = { attributes: ['+ id: string'], methods: ['+ save()'] };
        const size = classifierBoxSize({ type, label: `${meta.label} ${sameTypeCount + 1}`, data: extra });
        return magneticBlueprint(type, sameTypeCount, { width: Math.max(180, size.width), height: size.height }, extra);
      }
      case 'uml_interface': {
        const extra = { stereotype: 'interface', attributes: [] as string[], methods: ['+ execute()'] };
        const size = classifierBoxSize({ type, label: `${meta.label} ${sameTypeCount + 1}`, data: extra });
        return magneticBlueprint(type, sameTypeCount, { width: Math.max(180, size.width), height: size.height }, extra);
      }
      case 'uml_enum': {
        const extra = { stereotype: 'enumeration', attributes: ['VALUE_A', 'VALUE_B'], methods: [] as string[] };
        const size = classifierBoxSize({ type, label: `${meta.label} ${sameTypeCount + 1}`, data: extra });
        return magneticBlueprint(type, sameTypeCount, { width: Math.max(160, size.width), height: size.height }, extra);
      }
      case 'uml_actor':
        return magneticBlueprint(type, sameTypeCount, { width: 80, height: 120 });
      case 'uml_package':
        return magneticBlueprint(type, sameTypeCount, { width: 200, height: 120 });

      default: {
        const size = {
          width: NODE_WIDTH,
          height: calcNodeHeight(meta.inputs.length, meta.outputs.length),
        };
        return {
          size,
          sockets: buildSocketsFromType(type, NODE_WIDTH),
          label: `${meta.label} ${sameTypeCount + 1}`,
          data: { color: meta.color },
          skipAutoEdit: false,
        };
      }
    }
  }
}

function magneticBlueprint(
  type: string,
  sameTypeCount: number,
  size: Size,
  extra: Record<string, unknown> = {},
): NodeBlueprint {
  const meta = NODE_TYPES[type as keyof typeof NODE_TYPES] ?? NODE_TYPES.box;
  return {
    size,
    sockets: buildFourSidedSockets(size.width, size.height),
    label: `${meta.label} ${sameTypeCount + 1}`,
    data: { color: meta.color, ...extra },
    skipAutoEdit: false,
  };
}
